import { DefaultPrimaryButton, DefaultSecondaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { DetailCard } from '../../components/detail-card.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import ProcessOpenCloseSlots from '../../components/slot-open-displays/process-open-close-slots/process-open-close-slots.component';
import { useBulkMoveConfirm } from './bulk-move-confirm.controller';

const ID = 'bulk-move-confirm';

const BulkMoveConfirm = () => {
    const viewProps = useBulkMoveConfirm();

    if (viewProps.phase === 'source-opening' || viewProps.phase === 'drop-opening') {
        return (
            <div
                id={ID}
                className='bg-background-default flex size-full flex-col overflow-hidden'>
                <KioskNavigationHeader title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)} />
                <div className='flex flex-1 flex-col overflow-hidden'>
                    <ProcessOpenCloseSlots
                        key={viewProps.phase}
                        packages={viewProps.openingPackages}
                        onContinue={() => {
                            void viewProps.handleOpeningContinue();
                        }}
                    />
                </div>
            </div>
        );
    }

    if (viewProps.phase === 'done') {
        return (
            <div
                id={ID}
                className='bg-background-default flex size-full flex-col overflow-hidden'>
                <KioskNavigationHeader title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)} />
                <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
                    <p className='text-foreground-default text-lg font-semibold'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.move-done.title'
                            defaultMessage='Move finished with errors'
                            description='Title shown after bulk move finishes with one or more failed transactions'
                        />
                    </p>
                    <p className='text-foreground-muted text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.move-done.summary'
                            defaultMessage='{successCount, plural, one {# transaction moved} other {# transactions moved}}{failureCount, plural, =0 {} one { · # failed} other { · # failed}}.'
                            description='Summary after bulk move completes'
                            values={{ successCount: viewProps.successCount, failureCount: viewProps.failureCount }}
                        />
                    </p>
                </div>
                <KioskNavigationFooter
                    rightButton={
                        <DefaultPrimaryButton
                            text={CommonMessage.BUTTONS.CONTINUE}
                            onClick={() => {
                                void viewProps.handleOpeningContinue();
                            }}
                        />
                    }
                />
            </div>
        );
    }

    return (
        <div
            id={ID}
            className='bg-background-default flex size-full flex-col overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={viewProps.handleBack}
            />
            <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
                <p className='text-foreground-default text-lg font-semibold'>
                    <FormattedMessage
                        id='kiosk.manage-spot.bulk-move-transactions.move.confirm.title'
                        defaultMessage='Move transactions'
                        description='Title on the kiosk bulk move confirmation screen'
                    />
                </p>
                {viewProps.error && <div className='text-status-danger'>{viewProps.error}</div>}
                <DetailCard title={null}>
                    {viewProps.destinationSlotDisplay && (
                        <DetailCard.Row
                            label={
                                <FormattedMessage
                                    id='kiosk.manage-spot.bulk-move-transactions.confirm.destination-slot'
                                    defaultMessage='Destination SLOT'
                                    description='Label for the shared destination SLOT on bulk move confirm'
                                />
                            }
                            value={viewProps.destinationSlotDisplay}
                        />
                    )}
                    {viewProps.assignments.map((assignment) => (
                        <DetailCard.Row
                            key={assignment.key}
                            label={
                                assignment.colliLabel
                                    ? `${assignment.trackingNumber ?? assignment.transactionId} · ${assignment.colliLabel}`
                                    : (assignment.trackingNumber ?? assignment.transactionId)
                            }
                            value={null}
                        />
                    ))}
                </DetailCard>
            </div>
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={viewProps.handleBack}
                        disabled={viewProps.isSubmitting}
                    />
                }
                rightButton={
                    <DefaultPrimaryButton
                        text={CommonMessage.BUTTONS.CONFIRM}
                        onClick={() => {
                            void viewProps.handleConfirm();
                        }}
                        disabled={!viewProps.canConfirm}
                        isLoading={viewProps.isSubmitting}
                    />
                }
            />
        </div>
    );
};

export default BulkMoveConfirm;
